import { ControlledRadioGroup, type SelectionCardTint } from '@if/mui'
import { alpha, useTheme } from '@mui/material'
import type { JSX } from 'react'
import type { Control } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { CREDIT_RATING_BANDS, type CreditRatingBand } from '../hooks/creditRatingBands'
import type { StandaloneCalculatorValues } from '../standaloneCalculatorSchema'

// Best band green through worst band red. Taken from this app's own palette rather than
// `useRatingScale`, which needs the design system's `palette.rating1..6` — entries the
// back-office theme (a plain `createTheme`) does not define.
const RATING_ANCHOR_COUNT = 4

// Mirrors the tint weights @if/mui's `useRatingScale` applies to each anchor.
const TINT_WEIGHTS = {
  light: { background: 0.1, backgroundHover: 0.16, border: 0.4 },
  dark: { background: 0.16, backgroundHover: 0.24, border: 0.3 },
}

interface CreditRatingSelectorProps {
  control: Control<StandaloneCalculatorValues>
  disabled?: boolean
}

/**
 * The ERS band tiles: rating name above, ERS range below, best-rate-first from green to red.
 * The rate is deliberately not on the tile — only the credit engine knows what this merchant
 * would actually be given.
 */
const CreditRatingSelector = ({ control, disabled = false }: CreditRatingSelectorProps): JSX.Element => {
  const { t } = useTranslation()
  const theme = useTheme()

  const anchors = [
    theme.palette.success.main,
    theme.palette.info.main,
    theme.palette.warning.main,
    theme.palette.error.main,
  ]

  const tintFor = (index: number, total: number): SelectionCardTint => {
    const position = total <= 1 ? 0 : Math.round((index * (RATING_ANCHOR_COUNT - 1)) / (total - 1))
    const main = disabled ? theme.palette.action.disabled : anchors[position]
    const weights = TINT_WEIGHTS[theme.palette.mode]
    return {
      main,
      background: alpha(main, weights.background),
      backgroundHover: alpha(main, weights.backgroundHover),
      border: alpha(main, weights.border),
    }
  }

  const rangeLabel = (band: CreditRatingBand) =>
    band.maxErs === null
      ? t('calculator.ersBandOpen', { min: band.minErs })
      : t('calculator.ersBandRange', { min: band.minErs, max: band.maxErs })

  const options = CREDIT_RATING_BANDS.map((band, index) => ({
    value: band.minErs,
    title: t(band.nameKey),
    description: rangeLabel(band),
    tint: tintFor(index, CREDIT_RATING_BANDS.length),
  }))

  return (
    <ControlledRadioGroup
      control={control}
      name="ersScore"
      label={t('calculator.creditRatingTitle')}
      hideLabel
      options={options}
      disabled={disabled}
      variant="tile"
      sx={{
        display: 'grid',
        opacity: disabled ? 0.7 : 1,
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(5, minmax(0, 1fr))' },
        gap: 2,
      }}
    />
  )
}

export default CreditRatingSelector
